<template>
<div>
    <card>
        <template slot="body">
            <form :disabled="is_loading" @submit.prevent="onSubmit">
                <div class="mb-4 flex ai-fe jc-sb">
                    <h3 class="m-0">Facility Information</h3>

                    <button type="button" @click="load_from_zoho = true">
                        <i class="material-icons">get_app</i>
                        <span class="ml-1">Load from Zoho</span>
                    </button>
                </div>

                <div class="flex ai-fe jc-sb">
                    <div class="flex-33 mr-3">
                        <label for="state">Insurance Program</label>
                        <select-input
                            :active-index="insurance_program.active_index"
                            :choices="insurance_program.options"
                            @change="onChangeInsuranceProgram"></select-input>
                    </div>

                    <div class="flex-33 ml-3 mr-3">
                        <label for="facility-number">Facility Number</label>
                        <input :disabled="is_loading" id="facility-number" type="text" required v-model="facility.facility_number" />
                    </div>

                    <div class="flex-33 ml-3">
                        <label for="corporate-group">Corporate Group Number</label>
                        <input :disabled="is_loading" id="corporate-group" type="text" required v-model="facility.corporate_group" />
                    </div>
                </div>

                <div class="flex jc-sb mt-3">
                    <div class="flex-a">
                        <label for="account-name">Company Name</label>
                        <input :disabled="is_loading" id="account-name" type="text" required v-model="facility.account_name" />
                    </div>
                </div>

                <div class="mt-3">
                    <label for="address">Address</label>
                    <input :disabled="is_loading" id="address" type="text" required v-model="facility.address" />
                </div>

                <div class="flex jc-sb mt-3">
                    <div class="flex-33 mr-3">
                        <label for="city">City</label>
                        <input :disabled="is_loading" id="city" type="text" required v-model="facility.city" />
                    </div>

                    <div class="flex-33 ml-3 mr-3">
                        <label for="state">State</label>
                        <select-input
                            :active-index="active_state_index"
                            :choices="state_options"
                            :fixed="true"
                            @change="onChangeState"></select-input>
                    </div>

                    <div class="flex-33 ml-3">
                        <label for="zip-code">Zip Code</label>
                        <input :disabled="is_loading" id="zip-code" type="text" required v-model="facility.zip_code" />
                    </div>
                </div>

                <hr />

                <h3 class="mb-4">Manager Information</h3>

                <div class="flex jc-sb">
                    <div class="flex-50 mr-3">
                        <label for="contact-name">Manager Name</label>
                        <input :disabled="is_loading" id="contact-name" type="text" required v-model="facility.contact_name" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="email">Site Email</label>
                        <input :disabled="is_loading" id="email" type="email" required v-model="facility.email" />
                    </div>
                </div>

                <div class="flex jc-sb mt-3">
                    <div class="flex-50 mr-3">
                        <label for="phone">Phone</label>
                        <input :disabled="is_loading" id="phone" type="phone" required v-model="facility.phone" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="fax">Fax (optional)</label>
                        <input :disabled="is_loading" id="fax" type="text" v-model="facility.fax" />
                    </div>
                </div>

                <h4 class="mb-4">Additional Contacts</h4>

                <additional-contacts :contacts="additional_contacts" @change="onAdditionalContactsChange" />

                <hr />

                <h3 class="mb-4">Audit Letter Configuration</h3>

                <div>
                    <div class="flex ai-c jc-sb">
                        <span class="mr-2 ws-nw">For the audit letter, the facility</span>
                        <select-input
                            :active-index="uses_updated_lease_language.active_index"
                            :choices="uses_updated_lease_language.options"
                            :fixed="false"
                            @change="onChangeUsesUpdatedLeaseLanguage"></select-input>
                    </div>

                    <div class="flex ai-fs jc-sb mt-3">
                        <div class="flex-0 mr-3 ws-nw">
                            <label for="lease-section-number">Lease Language Section</label>
                            <input id="lease-section-number" type="text" name="lease-section-number" v-model="facility.lease_section_number" />
                        </div>

                        <div class="flex-a ml-3">
                            <label for="lease-section-text">Lease Language</label>
                            <textarea id="lease-section-text" name="lease-section-text" rows="4" v-model="facility.lease_section_text" style="resize: vertical;"></textarea>
                        </div>
                    </div>

                    <div class="flex jc-sb mt-3">
                        <div class="flex-50 mr-3 pos-r">
                            <label for="brochure-url">Brochure URL</label>
                            <input :disabled="is_loading" id="brochure-url" type="url" required v-model="facility.brochure_url" />

                            <a tabindex="-1" v-show="facility.brochure_url" class="url-link pos-a" :href="facility.brochure_url" target="_blank">
                                <i class="material-icons">launch</i>
                            </a>
                        </div>

                        <div class="flex-50 ml-3 pos-r">
                            <label for="certificate-url">Certificate URL</label>
                            <input :disabled="is_loading" id="certificate-url" type="url" required v-model="facility.certificate_url" />

                            <a tabindex="-1" v-show="facility.certificate_url" class="url-link pos-a" :href="facility.certificate_url" target="_blank">
                                <i class="material-icons">launch</i>
                            </a>
                        </div>
                    </div>
                </div>

                <hr />

                <div class="mb-4 flex ai-fe jc-sb">
                    <h3 class="m-0">Coverage Rate Options</h3>

                    <div class="flex ai-c jc-fe">
                        <div v-show="is_loading_coverages" class="flex ai-c mr-2">
                            <img src="../assets/img/loading-black-32.gif" alt="loading" />
                        </div>

                        <button :disabled="!facility.facility_number || is_loading_coverages" type="button" @click="loadCoverages('sitelink')">
                            <i class="material-icons">get_app</i>
                            <span class="ml-1">Load SiteLink Coverages</span>
                        </button>

                        <button :disabled="!facility.facility_number || is_loading_coverages" type="button" @click="loadCoverages('storedge')" class="ml-3">
                            <i class="material-icons">get_app</i>
                            <span class="ml-1">Load StorEdge Coverages</span>
                        </button>
                    </div>
                </div>

                <div>
                    <coverage-rate-options :options="coverages" @change="onCoverageOptionsChange" />

                    <p class="mt-5">Note: <b>coverage is required</b> to add a facility. You do not need to enter these in any particular order. They will be sorted as needed.</p>
                </div>

                <div class="flex jc-fe mt-5">
                    <div v-show="is_loading" class="flex ai-c mr-2">
                        <img src="../assets/img/loading-black-32.gif" alt="loading" />
                    </div>

                    <button :disabled="is_loading || !this.coverages.length" type="submit">Save</button>
                </div>
            </form>
        </template>
    </card>

    <modal v-show="show_modal" @close="onModalClose">
        <template slot="header">
            Success!
        </template>

        <template slot="content">
            A user account was also created for the facility's manager. Please inform the manager that he or she may log in with the credentials below.

            <hr />

            <table class="new-account-table" border="0" width="100%">
                <tr>
                    <td><strong>Username</strong></td>
                    <td>{{ new_user.username }}</td>
                </tr>
                <tr>
                    <td><strong>Password</strong></td>
                    <td>{{ new_user.password }}</td>
                </tr>
            </table>
        </template>
    </modal>

    <load-from-zoho-modal v-if="load_from_zoho" @close="load_from_zoho = false" @loaded="onLoadedFacility" />
</div>
</template>

<script>
import http from "@/http"
import { objectToParams } from "@/utilities/form-data"
import { statesByAbbr } from "@/utilities/states"

export default {
    name: "add-facility-page",

    data () {
        return {
            active_state_index: -1,
            additional_contacts: [],
            coverages: [],
            facility: { uses_updated_lease_language: true },
            new_user: {},
            insurance_program: {
                active_index: -1,
                options: []
            },
            is_loading: false,
            is_loading_coverages: false,
            load_from_zoho: false,
            show_modal: false,
            state_options: statesByAbbr,
            uses_updated_lease_language: {
                active_index: 1,
                options: [
                    { key: "false", label: "has correct lease language.", badge: null },
                    { key: "true", label: "needs updated lease language.", badge: null }
                ]
            }
        }
    },

    mounted () {
        this.loadInsurancePrograms()
    },

    methods: {
        async loadCoverages (software) {
            this.is_loading_coverages = true

            try {
                const params = {
                    facility_number: this.facility.facility_number,
                    software
                }

                const { data } = await http("/api/coverages/fetch-from-api", { params })

                switch (software) {
                case "sitelink":
                    this.coverages = this.coverages.filter((c) => c.sitelink_coverage_id === undefined)

                    for (const coverage of data) {
                        this.coverages.push({
                            coverage: parseFloat(coverage.dcCoverage),
                            premium: parseFloat(coverage.dcPremium),
                            sitelink_coverage_id: coverage.InsurCoverageID
                        })
                    }

                    break

                case "storedge":
                    this.coverages = this.coverages.filter((c) => c.storedge_coverage_id === undefined)

                    for (const coverage of data.invoiceable_items) {
                        const amount = coverage.amount
                        const coverageAmount = coverage.coverage_amount
                        const id = coverage.id

                        this.coverages.push({
                            coverage: parseFloat(coverageAmount),
                            premium: parseFloat(amount),
                            storedge_coverage_id: id
                        })
                    }

                    break

                default:
                    console.warn("Unexpected software type: %s", software)
                }
            } catch (ex) {
                if (ex.message) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex.message })
                } else {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                }
            } finally {
                this.is_loading_coverages = false
            }
        },

        async loadInsurancePrograms () {
            try {
                const { data } = await http("/api/insurance-programs")

                this.insurance_program.options = data.map((data) => {
                    return {
                        key: data.key,
                        badge: null,
                        label: data.label
                    }
                })

                for (const index in this.insurance_program.options) {
                    if (this.insurance_program.options[index].label.toLowerCase() === "pma") {
                        this.onChangeInsuranceProgram(index)
                    }
                }
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            }
        },

        onAdditionalContactsChange (contacts) {
            this.additional_contacts = contacts
        },

        /**
         * Update the state field when a state is picked from the list.
         *
         * @param int index Chose state option index
         */
        onChangeState (index) {
            this.active_state_index = index

            this.facility.state = this.state_options[index].key
        },

        onChangeInsuranceProgram (index) {
            this.insurance_program.active_index = index

            this.facility.insurance_program_key = this.insurance_program.options[index].key
        },

        onChangeUsesUpdatedLeaseLanguage (index) {
            this.uses_updated_lease_language.active_index = index

            this.facility.uses_updated_lease_language = this.uses_updated_lease_language.options[index].key
        },

        onCoverageOptionsChange (coverages) {
            this.coverages = coverages
        },

        onLoadedFacility (facility) {
            this.facility.facility_number = facility.Facility_Number
            this.facility.corporate_group = facility.Corporate_Group_Number
            this.facility.account_name = facility.Account_Name
            this.facility.address = facility.Address
            this.facility.city = facility.City
            this.facility.zip_code = facility.ZIP_Code
            this.facility.contact_name = facility.Property_Mgr
            this.facility.email = facility.Site_Email
            this.facility.phone = facility.Phone
            this.facility.fax = facility.Fax

            this.onChangeState(statesByAbbr.map(({ key }) => key).indexOf(facility.State))
        },

        onModalClose () {
            this.show_modal = false
            this.new_user = {}
            this.facility = { uses_updated_lease_language: false }
            this.additional_contacts = []
            this.coverages = []
            this.active_state_index = -1
        },

        async onSubmit () {
            const data = Object.assign({
                additional_contacts: JSON.stringify(this.additional_contacts),
                coverages: JSON.stringify(this.coverages)
            }, this.facility)
            const params = objectToParams(data)

            this.is_loading = true
            this.new_user = {}

            try {
                const { data: response } = await http.post("/api/facilities", params)

                this.new_user = { username: response.username, password: response.password }
                this.show_modal = true
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.is_loading = false
            }
        }
    }
}
</script>

<style scoped>
.new-account-table {
    border-collapse: collapse;
    table-layout: auto;
}

.new-account-table td {
    border: 1px solid #dedede;
    padding: 6px 8px;
}

.url-link {
    bottom: 11px;
    right: 12px;
}
</style>



// WEBPACK FOOTER //
// src/pages/AddFacilityPage.vue